import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Clock3, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { ChangeOffer, ChangeSource, SubscriptionChange } from "@shared/subscription-change";
import { useAdminObservation } from "@/hooks/use-admin-observation";

type PlanChangeRow = Pick<
  SubscriptionChange,
  "id" | "state" | "source" | "offer" | "effectiveAt" | "estimatedRefundCents" |
  "confirmedRefundCents" | "errorCode" | "accessActivated"
> & {
  userId: string;
  targetMembershipId: string | null;
  targetAccessActivated: boolean;
  actualRefundCents: number | null;
  verificationAttempts: number;
  updatedAt: string;
};

const QUERY_KEY = ["/api/admin/billing-plan-changes"] as const;

function dateLabel(value: string | null | undefined) {
  if (!value) return "Not available";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Not available" : date.toLocaleString();
}

function moneyLabel(cents: number | null | undefined) {
  if (cents == null) return "Not reported";
  return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(cents / 100);
}

function statusGroup(state: string) {
  if (state === "completed") return "Completed";
  if (state === "needs_attention") return "Needs attention";
  if (["quoted", "preparing", "awaiting_authorization", "scheduled", "verifying_target", "retiring_source", "refunding", "undoing"].includes(state)) return "Pending";
  return state.replaceAll("_", " ");
}

function stateStyle(state: string) {
  if (state === "completed") return "border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100";
  if (state === "needs_attention") return "border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100";
  return "border-slate-300 bg-slate-50 text-slate-800 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200";
}

export default function AdminBillingPlanChanges() {
  const queryClient = useQueryClient();
  const changes = useAdminObservation<PlanChangeRow[]>({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const response = await fetch("/api/admin/billing-plan-changes", { credentials: "include" });
      if (response.status === 410) throw new Error("Legacy plan-change history is retired and unavailable. Current Paddle pending operations and reconciliation failures are reported in Paddle billing health above. Retired provider actions remain disabled.");
      if (!response.ok) throw new Error("Could not load billing plan changes.");
      const rows = await response.json() as PlanChangeRow[];
      if (!Array.isArray(rows)) throw new Error("Billing plan change history was not a list.");
      return rows.slice(0, 100);
    },
    refetchInterval: 60_000,
  });

  const statusCheck = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`/api/admin/billing-plan-changes/${encodeURIComponent(id)}/reconcile`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Provider status check failed." }));
        throw new Error(error.message ?? "Provider status check failed.");
      }
      return { id, result: await response.json() as SubscriptionChange };
    },
    onSuccess: ({ id, result }) => {
      queryClient.setQueryData<PlanChangeRow[]>(QUERY_KEY, old => old?.map(row => row.id === id ? {
        ...row,
        state: result.state,
        source: result.source as ChangeSource,
        offer: result.offer as ChangeOffer,
        effectiveAt: result.effectiveAt,
        estimatedRefundCents: result.estimatedRefundCents,
        confirmedRefundCents: result.confirmedRefundCents,
        errorCode: result.errorCode,
        targetAccessActivated: result.accessActivated ?? row.targetAccessActivated,
        accessActivated: result.accessActivated,
      } : row));
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  return (
    <Card data-testid="admin-billing-plan-changes">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Clock3 className="h-4 w-4 text-muted-foreground" />
              Plan change operations
            </CardTitle>
            <p className="mt-1 max-w-2xl text-xs text-muted-foreground">
              Legacy bounded history: latest 100 changes, when available. Current Paddle operations are summarized in billing health above.
              Status checks read provider state only; they do not charge or retry a payment.
              The automatic worker completes financial steps the customer already consented to.
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              History received: {changes.dataUpdatedAt ? new Date(changes.dataUpdatedAt).toLocaleString() : "not available"}. Failed or stale observations are unknown, not an empty history.
            </p>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={changes.isFetching}
            onClick={() => void changes.refetch()}
          >
            <RefreshCw className={`mr-2 h-3.5 w-3.5 ${changes.isFetching ? "animate-spin" : ""}`} />
            Refresh history
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {changes.isLoading ? (
          <div aria-label="Loading billing plan changes" className="space-y-2">
            <div className="h-16 animate-pulse rounded-lg bg-muted" />
            <div className="h-16 animate-pulse rounded-lg bg-muted" />
          </div>
        ) : changes.isError ? (
          <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm text-rose-950 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-100">
            <span>{changes.error instanceof Error ? changes.error.message : "Could not load billing plan changes."}</span>
            <Button type="button" size="sm" variant="outline" onClick={() => void changes.refetch()}>Retry history</Button>
          </div>
        ) : !changes.data?.length ? (
          <div className="rounded-lg border border-dashed p-6 text-center">
            <CheckCircle2 className="mx-auto h-5 w-5 text-emerald-600" />
            <p className="mt-2 text-sm font-medium">No plan changes in the retained history.</p>
            <p className="mt-1 text-xs text-muted-foreground">New operational records will appear here.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {changes.data.slice(0, 100).map(row => {
              const accessActive = row.targetAccessActivated ?? row.accessActivated ?? false;
              const checking = statusCheck.isPending && statusCheck.variables === row.id;
              return (
                <article key={row.id} className="rounded-xl border bg-card p-3 sm:p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${stateStyle(row.state)}`}>
                          {statusGroup(row.state)}
                        </span>
                        <span className="text-xs text-muted-foreground">state: {row.state.replaceAll("_", " ")}</span>
                      </div>
                      <h3 className="mt-2 text-sm font-semibold">
                        Current: {row.source.tier} {row.source.interval ?? "interval unavailable"} <span className="text-muted-foreground">→</span>{" "}
                        Target: {row.offer.tier} {row.offer.interval}
                      </h3>
                      <p className="mt-1 break-all text-[11px] text-muted-foreground">Change {row.id} · user {row.userId}</p>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={statusCheck.isPending}
                      onClick={() => statusCheck.mutate(row.id)}
                    >
                      <RefreshCw className={`mr-2 h-3.5 w-3.5 ${checking ? "animate-spin" : ""}`} />
                      {checking ? "Checking provider…" : "Check provider status"}
                    </Button>
                  </div>
                  <div className="mt-3 grid gap-3 border-t pt-3 text-xs sm:grid-cols-2 lg:grid-cols-4">
                    <div>
                      <p className="text-muted-foreground">Effective boundary</p>
                      <p className="mt-0.5 font-medium">{dateLabel(row.effectiveAt)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Target access activated</p>
                      <p className={`mt-0.5 font-medium ${accessActive ? "text-emerald-700 dark:text-emerald-300" : ""}`}>
                        {accessActive ? "Activated" : "Not activated"}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Verification attempts</p>
                      <p className="mt-0.5 font-medium">{row.verificationAttempts}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Last updated</p>
                      <p className="mt-0.5 font-medium">{dateLabel(row.updatedAt)}</p>
                    </div>
                  </div>
                  <div className="mt-3 rounded-lg bg-muted/50 p-3">
                    <div className="flex items-center gap-2 text-xs font-semibold">
                      {row.state === "needs_attention" ? <AlertTriangle className="h-3.5 w-3.5 text-amber-600" /> : <Clock3 className="h-3.5 w-3.5 text-muted-foreground" />}
                      Separate refund status
                    </div>
                    <div className="mt-2 grid gap-2 text-xs sm:grid-cols-3">
                      <p><span className="text-muted-foreground">Estimated:</span> {moneyLabel(row.estimatedRefundCents)}</p>
                      <p><span className="text-muted-foreground">Actual:</span> {moneyLabel(row.actualRefundCents)}</p>
                      <p><span className="text-muted-foreground">Confirmed:</span> {moneyLabel(row.confirmedRefundCents)}</p>
                    </div>
                    <p className="mt-2 text-[11px] text-muted-foreground">Estimated refunds are not guaranteed. Actual and confirmed amounts are reported separately.</p>
                  </div>
                  {row.errorCode && (
                    <p className={`mt-3 text-xs ${row.state === "needs_attention" ? "text-amber-800 dark:text-amber-200" : "text-muted-foreground"}`}>
                      <span className="font-semibold">Attention code:</span> {row.errorCode}
                    </p>
                  )}
                </article>
              );
            })}
          </div>
        )}
        {statusCheck.isError && (
          <p role="alert" className="mt-3 text-xs text-rose-700 dark:text-rose-300">
            {statusCheck.error instanceof Error ? statusCheck.error.message : "Provider status check failed."}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
